import {
  AsYouType,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js";

/**
 * Phone helpers backed by libphonenumber-js (Google's libphonenumber port) — replaces
 * the hand-written per-country masks. Country reference data (name/flag) comes from the
 * owned shipping allowlist; the calling code + as-you-type formatting come from here.
 */

/** Calling code for a country ISO-2 (e.g. "US" → "+1"); "" when it's not a known region. */
export function dialCode(iso: string): string {
  return isSupportedCountry(iso) ? `+${getCountryCallingCode(iso)}` : "";
}

/**
 * Format a raw phone value as-you-type for the given country; passthrough if unknown.
 * Caps the national digits to the country's max valid length first — past that,
 * libphonenumber's as-you-type formatter gives up and returns the digits as one
 * unformatted run, so the cap keeps the mask intact (and blocks over-typing).
 */
export function formatPhone(raw: string, iso: string): string {
  if (!isSupportedCountry(iso)) return raw;
  let digits = raw.replace(/\D/g, "");
  while (digits && validatePhoneNumberLength(digits, iso) === "TOO_LONG") {
    digits = digits.slice(0, -1);
  }
  return new AsYouType(iso).input(digits);
}

/** True when `value` is a valid phone number for the country (strict — real, callable number). */
export function isValidPhone(value: string, iso: string): boolean {
  return isSupportedCountry(iso) && isValidPhoneNumber(value, iso);
}

/** Canonical E.164 (e.g. "+12025551234") for storage / Shopify; passthrough if unparseable. */
export function toE164(value: string, iso: string): string {
  if (!isSupportedCountry(iso)) return value;
  return parsePhoneNumberFromString(value, iso)?.number ?? value;
}
